import { isPlatformBrowser } from '@angular/common';
import { Component, inject, type OnDestroy, type OnInit, PLATFORM_ID } from '@angular/core';
import { SharedModule } from '@shared/modules/shared.module';

/** Chrome's install prompt (not in the DOM typings) */
interface BeforeInstallPromptEvent extends Event {
	prompt(): Promise<void>;
	userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

declare global {
	interface Window {
		/** Captured in index.html: Chrome may fire it before Angular starts */
		__installPrompt?: BeforeInstallPromptEvent | null;
	}
}

const DISMISSED_KEY = 'installBanner.dismissedAt'; // i18n-ignore: localStorage key, not a translation
/** Closed by hand: not shown again for this long */
const DISMISS_MS = 30 * 24 * 60 * 60 * 1000;

/**
 * Slim hint to install the web app, for logged-in users on a phone browser. A browser cannot tell whether
 * the app is already installed, only whether it is running installed (standalone): there it never shows.
 * iPhone: Share → Add to Home Screen. Android: Chrome's own install prompt, or the browser menu.
 */
@Component({
	selector: 'app-install-banner',
	imports: [SharedModule],
	templateUrl: './install-banner.component.html',
	styleUrl: './install-banner.component.scss',
})
export class InstallBannerComponent implements OnInit, OnDestroy {
	private platformId = inject(PLATFORM_ID);

	visible = false;
	device: 'ios' | 'android' = 'android';
	installPrompt: BeforeInstallPromptEvent | null = null;

	private readonly onPrompt = (event: Event) => {
		event.preventDefault();
		this.installPrompt = event as BeforeInstallPromptEvent;
	};
	private readonly onInstalled = () => {
		this.visible = false;
	};

	ngOnInit(): void {
		if (!isPlatformBrowser(this.platformId) || this.isStandalone() || this.recentlyDismissed()) return;

		this.device = this.isIos() ? 'ios' : 'android';
		this.installPrompt = window.__installPrompt ?? null;
		window.addEventListener('beforeinstallprompt', this.onPrompt);
		window.addEventListener('appinstalled', this.onInstalled);
		this.visible = true;
	}

	ngOnDestroy(): void {
		if (!isPlatformBrowser(this.platformId)) return;
		window.removeEventListener('beforeinstallprompt', this.onPrompt);
		window.removeEventListener('appinstalled', this.onInstalled);
	}

	async install(): Promise<void> {
		if (!this.installPrompt) return;
		await this.installPrompt.prompt();
		const { outcome } = await this.installPrompt.userChoice;
		// The prompt can only be used once
		this.installPrompt = null;
		window.__installPrompt = null;
		if (outcome === 'accepted') this.visible = false;
	}

	dismiss(): void {
		this.visible = false;
		try {
			localStorage.setItem(DISMISSED_KEY, String(Date.now()));
		} catch {
			// private mode / blocked storage: it just shows again next time
		}
	}

	private isStandalone(): boolean {
		const iosStandalone = (navigator as Navigator & { standalone?: boolean }).standalone === true;
		return iosStandalone || window.matchMedia('(display-mode: standalone)').matches;
	}

	private isIos(): boolean {
		// iPadOS reports itself as a Mac with touch
		return (
			/iPhone|iPad|iPod/i.test(navigator.userAgent) ||
			(/Macintosh/.test(navigator.userAgent) && navigator.maxTouchPoints > 1)
		);
	}

	private recentlyDismissed(): boolean {
		try {
			const at = Number(localStorage.getItem(DISMISSED_KEY));
			return at > 0 && Date.now() - at < DISMISS_MS;
		} catch {
			return false;
		}
	}
}
